import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, Button, Autocomplete, Snackbar, Alert, Grid, CircularProgress } from '@mui/material';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  pharmacyNum: string;
}

interface TableField {
  id: number;
  cName: string;
  cType: string;
  displayName: string;
  InputBlock: number;
  formula: string;
  sortByGroup: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
}

interface EditableData {
  [key: string]: string | number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const EditReport: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [tableFields, setTableFields] = useState<TableField[]>([]);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editDate, setEditDate] = useState<string>('');
  const [editableData, setEditableData] = useState<EditableData | null>(null);
  const [editableFields, setEditableFields] = useState<TableField[]>([]);
  // const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveAndRecalculateLoading, setIsSaveAndRecalculateLoading] = useState(false);
  const [previousData, setPreviousData] = useState<EditableData | null>(null);
  const [pastValue, setPastValue] = useState<string>('');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    document.title = 'Редактировать отчет | Zerde';
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const logAction = async (action: string) => {
    const currentRole = localStorage.getItem('role') || 'unknown';
    const currentUsername = localStorage.getItem('username') || 'unknown';
    try {
      await axios.post('https://tbl.zerdegroup.kz/api/log', {
        role: currentRole,
        username: currentUsername,
        action
      });
    } catch (error) {
      console.error('Ошибка при логировании действия:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchTableStructure();
  }, []);

  useEffect(() => {
    if (!editUser || !editDate) {
      setEditableData(null);
      return;
    }

    let isStale = false;

    const loadDataForEffect = async (user: User, date: string) => {
      setIsLoading(true);
      setEditableData(null); 

      try {
        const fieldsResponse = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
        if (isStale) return;

        const fields = fieldsResponse.data.filter((field: TableField) => field.hideInStatTable === 0 || field.hideInStatTable === 1);
        
        const dataResponse = await axios.get('https://tbl.zerdegroup.kz/api/get-user-data', {
          params: { username: user.username, date: date }
        });
        if (isStale) return;

        await logAction(`запросил данные для пользователя ${user.username} за дату ${date} | Редактировать отчет`);

        let filledData: EditableData = {};
        if (dataResponse.data.success && dataResponse.data.exists) {
          const userData = dataResponse.data.data;
          fields.forEach((field: TableField) => {
            if (userData.hasOwnProperty(field.cName)) {
              filledData[field.cName] = formatValue(userData[field.cName], field.cType);
            } else if (field.cName === 'pharmacyNum') {
              filledData[field.cName] = user.pharmacyNum;
            } else if (field.cName === 'date') {
              filledData[field.cName] = date;
            } else {
              filledData[field.cName] = formatValue('', field.cType);
            }
          });
        } else {
          fields.forEach((field: TableField) => {
            if (field.cName === 'pharmacyNum') {
              filledData[field.cName] = user.pharmacyNum;
            } else if (field.cName === 'date') {
              filledData[field.cName] = date;
            } else {
              filledData[field.cName] = formatValue('', field.cType);
            }
          });
        }

        const pastValueResponse = await axios.get('https://tbl.zerdegroup.kz/api/new-value', {
          params: { username: user.username, date: date }
        });
        if (isStale) return;

        if (!isStale) {
          setEditableFields(fields);
          setPastValue(pastValueResponse.data.success ? pastValueResponse.data.value : '0');
          setEditableData(filledData);
          setPreviousData({ ...filledData });
        }
      } catch (error) {
        if (!isStale) {
          console.error('Ошибка при загрузке данных для редактирования:', error);
          setSnackbar({
            open: true,
            message: 'Ошибка при загрузке данных для редактирования',
            severity: 'error',
          });
        }
      } finally {
        if (!isStale) {
          setIsLoading(false);
        }
      }
    };

    loadDataForEffect(editUser, editDate);

    return () => {
      isStale = true;
    };
  }, [editUser, editDate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy');
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        setSnackbar({
          open: true,
          message: 'Не удалось загрузить список пользователей',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при загрузке пользователей',
        severity: 'error',
      });
    }
  };

  const fetchTableStructure = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
      setTableFields(response.data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при получении структуры таблицы:' + error,
        severity: 'error',
      });
      console.error('Ошибка при получении структуры таблицы:', error);
    }
  };

  const handleEditUserChange = (event: React.SyntheticEvent, value: User | null) => {
    setEditUser(value);
  };

  const handleEditDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setEditDate(newDate);
  };

  const safeEvaluate = (expression: string): number => {
    const sanitizedExpr = expression.replace(/\s+/g, '');
    if (!/^[0-9+\-*/().]+$/.test(sanitizedExpr)) {
      throw new Error('Недопустимое выражение');
    }

    try {
      // eslint-disable-next-line
      const result = new Function(`return ${sanitizedExpr}`)();
      if (typeof result !== 'number' || !isFinite(result)) {
        throw new Error('Недопустимый результат');
      }
      return result;
    } catch {
      throw new Error('Ошибка вычисления');
    }
  };

  const evaluateFormula = useCallback((formula: string, fieldType: string): string => {
    if (formula.startsWith('{') && formula.endsWith('}')) {
      const variableName = formula.slice(1, -1);
      switch (variableName) {
        case 'pharmacyNum':
          return editUser?.pharmacyNum || '';
        case 'serverDate':
          return fieldType.toLowerCase() === 'datetime' ? editDate : editDate.split('-').reverse().join('.');
        case 'pastValueEndDay':
          return pastValue;
        default:
          return '';
      }
    }

    try {
      const fieldPattern = /(\w+)\((\d+)\)/g;
      let evaluatedFormula = formula.replace(fieldPattern, (_, fieldName) => {
        const fieldValue = editableData?.[fieldName] || '0';
        return fieldValue.toString();
      });

      const result = safeEvaluate(evaluatedFormula);
      return result.toString();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при вычислении формулы:' + error,
        severity: 'error',
      });
      console.error('Ошибка при вычислении формулы:', error);
      return '';
    }
  }, [editableData, editUser, editDate, pastValue]);

  const calculateFormulas = useCallback(() => {
    if (!editableData) return;

    const newData = { ...editableData };
    let hasChanges = false;

    editableFields.forEach(field => {
      if (field.formula && field.InputBlock === 1) {
        const calculatedValue = evaluateFormula(field.formula, field.cType);
        if (newData[field.cName] !== calculatedValue) {
          newData[field.cName] = calculatedValue;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setEditableData(newData);
    }
  }, [editableData, editableFields, evaluateFormula]);

  const formatNumberWithSpaces = (value: string | number, fieldType: string): string => {
    if (fieldType === 'datetime' || fieldType === 'date') {
      return String(value);
    }

    if (value == null) return '';

    const strValue = String(value);
    const [intPart, fracPart] = strValue.split('.');
    const formattedIntPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return fracPart ? `${formattedIntPart}.${fracPart}` : formattedIntPart;
  };

  const cleanNumberString = (value: string, fieldType: string): string => {
    if (fieldType === 'datetime' || fieldType === 'date') {
      return value;
    }
    return value.replace(/\s/g, '');
  };

  const handleEditableInputChange = (fieldName: string, value: string, fieldType: string) => {
    if (!editableData) return;
  
    let processedValue = value;
  
    if (fieldType.toLowerCase() === 'float') {
      processedValue = processedValue
        .replace(',', '.')
        .replace(/[^\d.-]/g, '');
  
      const isValid = /^-?\d*\.?\d*$/.test(processedValue);
      if (processedValue === '' || isValid) {
        setEditableData(prev => ({
          ...prev,
          [fieldName]: processedValue
        }));
      }
    } else if (fieldType.toLowerCase() === 'int') {
      processedValue = processedValue.replace(/[^\d-]/g, '');
  
      const isValid = /^-?\d*$/.test(processedValue);
      if (processedValue === '' || isValid) {
        setEditableData(prev => ({
          ...prev,
          [fieldName]: processedValue
        }));
      }
    } else {
      setEditableData(prev => ({
        ...prev,
        [fieldName]: value
      }));
    }
  };
  

  const handleBlur = (fieldName: string, fieldType: string) => {
    if (editableData) {
      const currentValue = editableData[fieldName];

      if (currentValue == null || String(currentValue).trim() === '') {
        setEditableData(prev => ({
          ...prev,
          [fieldName]: formatValue('', fieldType)
        }));
        return;
      }
      if (fieldType === 'datetime' || fieldType === 'date') {
        return;
      }

      if (fieldType.toLowerCase() === 'float') {
        const cleanedValue = cleanNumberString(String(currentValue), fieldType);
        const num = parseFloat(cleanedValue);
        if (!isNaN(num)) {
          setEditableData(prev => ({
            ...prev,
            [fieldName]: formatNumberWithSpaces(num.toFixed(2), fieldType)
          }));
        } else {
          setEditableData(prev => ({
            ...prev,
            [fieldName]: '0.00'
          }));
        }
      } else if (fieldType.toLowerCase() === 'int') {
        const cleanedValue = cleanNumberString(String(currentValue), fieldType);
        const num = parseInt(cleanedValue);
        if (!isNaN(num)) {
          setEditableData(prev => ({
            ...prev,
            [fieldName]: formatNumberWithSpaces(String(num), fieldType)
          }));
        } else {
          setEditableData(prev => ({
            ...prev,
            [fieldName]: '0'
          }));
        }
      }
    }
  };

  const formatValue = (value: string | number, type: string): string => {
    if (value == null || String(value).trim() === '') {
      if (type.toLowerCase() === 'int') return '0';
      if (type.toLowerCase() === 'float') return '0.00';
      return '';
    }

    if (type.toLowerCase() === 'float') {
      const num = parseFloat(String(value));
      if (!isNaN(num)) {
        return num.toFixed(2);
      }
      return '0.00';
    }

    if (type.toLowerCase() === 'int') {
      const num = parseInt(String(value));
      if (!isNaN(num)) {
        return String(num);
      }
      return '0';
    }

    return String(value);
  };

  const isFieldEmpty = (value: string | number, fieldType: string): boolean => {
    if (value == null) return true;

    const strValue = String(value);

    if (strValue.trim() === '') return true;

    if (fieldType.toLowerCase() === 'float') {
      const numValue = parseFloat(strValue);
      return isNaN(numValue) || numValue === 0;
    }

    if (fieldType.toLowerCase() === 'int') {
      const numValue = parseInt(strValue);
      return isNaN(numValue) || numValue === 0;
    }

    return false;
  };

  const validateForm = useCallback(() => {
    if (!editableData) {
      return;
    }

    const requiredFields = editableFields.filter(field => field.important === 1);
    const isValid = requiredFields.every(field => {
      const value = editableData[field.cName];
      return !isFieldEmpty(value, field.cType);
    });
  }, [editableData, editableFields]);

  useEffect(() => {
    validateForm();
    calculateFormulas();
  }, [editableData, validateForm, calculateFormulas]);


  const randomDelay = (min: number, max: number): Promise<void> => {
    const delay = Math.floor(Math.random() * (max - min + 1)) + min;
    return new Promise(resolve => setTimeout(resolve, delay));
  };
  
  const handleSaveAndRecalculate = async () => {
    if (!editableData || !previousData) return;

    const processedFormValues = Object.entries(editableData).reduce((acc, [key, value]) => {
      const field = editableFields.find(f => f.cName === key);
      if (field) {
        if (field.cName === 'date') {
          if (typeof value === 'string' && value.includes('.')) {
            const [day, month, year] = value.split('.');
            acc[key] = `${year}-${month}-${day}`;
          } else {
            acc[key] = value;
          }
        } else {
          const cleanedValue = typeof value === 'string' ? value.replace(/\s/g, '') : value;
          acc[key] = cleanedValue === ''
            ? (field.cType.toLowerCase() === 'float' ? 0.00 : 0)
            : cleanedValue;
        }
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string | number>);

    setIsLoading(false);
    setIsSaveAndRecalculateLoading(true);

    try {
      // сначала выполнить сохранение
      const saveResponse = await axios.post('https://tbl.zerdegroup.kz/api/save-data', {
        username: editUser?.username,
        formData: processedFormValues
      });

      if (saveResponse.data.success) {
        const changedFields = editableFields
          .filter(field => {
            const oldValue = previousData[field.cName];
            const newValue = editableData[field.cName];
            return oldValue !== newValue;
          })
          .map(field => {
            const oldValue = previousData[field.cName];
            const newValue = editableData[field.cName];
            return `${field.displayName} (${oldValue} ➔ ${newValue})`;
          });

        const changesMessage = changedFields.length > 0
          ? ` Изменены поля: ${changedFields.join(', ')}`
          : '';

        await logAction(
          `изменил данные для пользователя ${editUser?.username} за дату ${editDate}.${changesMessage} | Редактировать отчет`
        );

        // задержка 
        await randomDelay(100, 1000);

        // запустить пересчет
        const recalculateResponse = await axios.post('https://tbl.zerdegroup.kz/api/recalculate-future-data', {
          username: editUser?.username,
          startDate: editDate,
          currentData: editableData
        });

        if (recalculateResponse.data.success) {
          await logAction(
            `выполнил пересчет данных для пользователя ${editUser?.username} начиная с даты ${editDate} | Редактировать отчет`
          );

          setSnackbar({
            open: true,
            message: 'Данные успешно сохранены и пересчитаны',
            severity: 'success',
          });

          setPreviousData({ ...editableData });
        } else {
          throw new Error(recalculateResponse.data.message || 'Ошибка при пересчете данных');
        }
      } else {
        throw new Error('Ошибка при сохранении данных');
      }
    } catch (err) {
      console.error('Ошибка:', err);
      setSnackbar({
        open: true,
        message: (err instanceof Error ? err.message : 'Ошибка при сохранении и пересчете данных'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
      setIsSaveAndRecalculateLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', gap: '20px', alignItems: 'flex-end', mt: 1 }}>
        <Autocomplete
          options={users}
          getOptionLabel={(option) => `${option.username} (Аптека: ${option.pharmacyNum})`}
          renderInput={(params) => <TextField {...params} label="Выберите пользователя" />}
          onChange={handleEditUserChange}
          value={editUser}
          size='small'
          sx={{
            width: 300,
          }}
        />
        <TextField
          label="Дата отчета"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={editDate}
          size='small'
          onChange={handleEditDateChange}
        />
      </Box>

      {isLoading && !isSaveAndRecalculateLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && editableData && editableFields.length > 0 && (
        <Box>
          <Grid container spacing={2}>
            {editableFields.map((field) => (
              <Grid item xs={12} sm={6} md={2} key={field.cName}>
                <TextField
                  fullWidth
                  label={field.displayName}
                  value={formatNumberWithSpaces(editableData[field.cName] || '', field.cType)}
                  onChange={(e) => handleEditableInputChange(field.cName, e.target.value, field.cType)}
                  onBlur={() => handleBlur(field.cName, field.cType)}
                  disabled={field.InputBlock === 1 || field.cName === 'pharmacyNum' || field.cName === 'date'}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  type="text"
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 2, width: 1 }}>
          <Button
              variant="contained"
              color='error'
              onClick={handleSaveAndRecalculate}
              disabled={isSaveAndRecalculateLoading}
              sx={{p: 1, width: 250}}
            >
              {isSaveAndRecalculateLoading ? (
                <CircularProgress size={24} />
              ) : (
                'Сохранить и пересчитать'
              )}
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditReport;